/**
 * Scene documents (`*.scene.json`, `prism.scene/1`) and the editor commands
 * that mutate them.
 *
 * This mirrors `prism_core::editor::apply_command` in the engine exactly, so
 * the hierarchy and inspector can edit the document without the WASM runtime
 * and produce the same result the engine would. Pure and synchronous: every
 * function returns a new document and never mutates its input.
 */

import { composeMatrix, decomposeMatrix, invertAffine, multiply, worldMatrix } from './transform-math.js';

export type Vec3 = [number, number, number];

export interface TransformDesc {
  position?: Vec3;
  /** Euler XYZ, degrees. */
  rotation?: Vec3;
  scale?: Vec3;
}

export type Components = Record<string, unknown>;

export interface EntityDesc {
  id: string;
  name?: string | null;
  parent?: string | null;
  transform?: TransformDesc;
  components?: Components;
  [extra: string]: unknown;
}

export interface SceneDoc {
  format?: string;
  name?: string;
  environment?: Record<string, unknown>;
  entities: EntityDesc[];
  [extra: string]: unknown;
}

/** JSON shape of prism's `EditorCommand` (`{"cmd": "...", ...}`). */
export type EditorCommand =
  | { cmd: 'select'; ids: string[] }
  | { cmd: 'setTransform'; id: string; transform: TransformDesc }
  | { cmd: 'addEntity'; desc: EntityDesc; parent?: string | null }
  | { cmd: 'deleteEntity'; id: string }
  | { cmd: 'renameEntity'; id: string; name: string }
  | { cmd: 'setComponent'; id: string; key: string; value: unknown }
  | { cmd: 'removeComponent'; id: string; key: string }
  | { cmd: 'duplicate'; id: string; newId?: string | null }
  | { cmd: 'reparent'; id: string; parent: string | null }
  /** Terrain data edit (the engine saves the `*.terrain.json`; the scene is unchanged). */
  | { cmd: 'terrainEdit'; id: string; edit: Record<string, unknown> }
  /**
   * Clipboard entities ({@link copyEntities}): roots carry world transforms and
   * go under `parent`; `keepWorld` (default true) converts them to its space.
   */
  | { cmd: 'paste'; entities: EntityDesc[]; parent?: string | null; keepWorld?: boolean }
  /** Several commands as one edit (one undo step); all or nothing. */
  | { cmd: 'batch'; commands: EditorCommand[] };

export type CommandResult =
  | {
      ok: true;
      doc: SceneDoc;
      /** id created by addEntity/duplicate (the first root for paste / batch) */
      created?: string;
      /** Root ids created by addEntity / duplicate / paste / batch. */
      createdIds?: string[];
      /** Same edit with every generated id spelled out (what the engine is sent). */
      resolved?: EditorCommand;
    }
  | { ok: false; error: string; doc: SceneDoc };

export const IDENTITY: Required<TransformDesc> = {
  position: [0, 0, 0],
  rotation: [0, 0, 0],
  scale: [1, 1, 1],
};

export function fullTransform(t: TransformDesc | undefined): Required<TransformDesc> {
  return {
    position: t?.position ?? IDENTITY.position,
    rotation: t?.rotation ?? IDENTITY.rotation,
    scale: t?.scale ?? IDENTITY.scale,
  };
}

export function emptyScene(name = 'Main'): SceneDoc {
  return {
    format: 'prism.scene/1',
    name,
    environment: { skybox: { type: 'procedural' }, ambient: { source: 'skybox', intensity: 1 } },
    entities: [],
  };
}

/** Parse scene JSON text. Throws with a readable message when it is not a scene. */
export function parseScene(text: string): SceneDoc {
  const value: unknown = JSON.parse(text);
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Scene file is not a JSON object.');
  }
  const doc = value as SceneDoc;
  if (doc.entities !== undefined && !Array.isArray(doc.entities)) {
    throw new Error('"entities" must be an array.');
  }
  return { ...doc, entities: Array.isArray(doc.entities) ? doc.entities : [] };
}

export function serializeScene(doc: SceneDoc): string {
  return `${JSON.stringify(doc, null, 2)}\n`;
}

export function entityName(entity: EntityDesc): string {
  return entity.name || entity.id;
}

function indexOf(doc: SceneDoc, id: string): number {
  return doc.entities.findIndex((e) => e.id === id);
}

/** Closest id by edit distance, for "did you mean" hints. */
function suggest(target: string, candidates: string[]): string | null {
  let best: string | null = null;
  let bestScore = Infinity;
  for (const c of candidates) {
    const d = levenshtein(target, c);
    if (d < bestScore) {
      bestScore = d;
      best = c;
    }
  }
  return best !== null && bestScore <= Math.max(2, Math.floor(target.length / 3)) ? best : null;
}

function levenshtein(a: string, b: string): number {
  const row = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    let prev = row[0]!;
    row[0] = i;
    for (let j = 1; j <= b.length; j += 1) {
      const tmp = row[j]!;
      row[j] = Math.min(row[j]! + 1, row[j - 1]! + 1, prev + (a[i - 1] === b[j - 1] ? 0 : 1));
      prev = tmp;
    }
  }
  return row[b.length]!;
}

function notFound(doc: SceneDoc, id: string): string {
  const hint = suggest(
    id,
    doc.entities.map((e) => e.id),
  );
  return `entity '${id}' not found${hint ? `; did you mean '${hint}'?` : ''}`;
}

/** Ids of `id` and all its descendants, in document order. */
export function subtreeIds(doc: SceneDoc, id: string): string[] {
  const set = new Set([id]);
  for (;;) {
    const before = set.size;
    for (const e of doc.entities) {
      if (e.parent && set.has(e.parent)) set.add(e.id);
    }
    if (set.size === before) break;
  }
  return doc.entities.filter((e) => set.has(e.id)).map((e) => e.id);
}

/** `base`, else `base_2`, `base_3`, ... — same as prism's `unique_with`. */
export function uniqueWith(base: string, taken: (candidate: string) => boolean): string {
  if (!taken(base)) return base;
  for (let n = 2; ; n += 1) {
    const candidate = `${base}_${n}`;
    if (!taken(candidate)) return candidate;
  }
}

export function slug(text: string): string {
  const s = text
    .split('')
    .map((c) => (/[A-Za-z0-9]/.test(c) ? c.toLowerCase() : '_'))
    .join('')
    .replace(/^_+|_+$/g, '');
  return s || 'entity';
}

/**
 * Clipboard copy of `ids` with their descendants, in document order
 * (prism_core `copy_entities`): roots get their world transform and no parent.
 */
export function copyEntities(doc: SceneDoc, ids: readonly string[]): EntityDesc[] {
  const set = new Set<string>();
  for (const id of ids) {
    if (indexOf(doc, id) >= 0 && !set.has(id)) for (const d of subtreeIds(doc, id)) set.add(d);
  }
  return doc.entities
    .filter((e) => set.has(e.id))
    .map((e) => {
      const c = clone(e);
      if (!(e.parent && set.has(e.parent))) {
        c.parent = null;
        c.transform = decomposeMatrix(worldMatrix(doc, e.id));
      }
      return c;
    });
}

/** Root ids `after` has and `before` lacks. */
export function createdRoots(before: SceneDoc, after: SceneDoc): string[] {
  const old = new Set(before.entities.map((e) => e.id));
  return after.entities.filter((e) => !old.has(e.id) && !(e.parent && !old.has(e.parent))).map((e) => e.id);
}

function clone<T>(value: T): T {
  return value === undefined ? value : (JSON.parse(JSON.stringify(value)) as T);
}

/** Apply one command. On error the returned document is the input, unchanged. */
/* ------------------------------------------------------------------ */
/* Prefab instances: entities below an instance ("<instance>/<child>") are
 * not in the document; commands on them become overrides on the instance's
 * `prefab` component (prism_core prefab_overrides.rs / editor.rs). */

/** One per-instance prefab override (`prefab.overrides[]`). */
export interface PrefabOverride {
  /** Prefab entity relative to the instance (`""` = root, `"seat/cushion"` nested). */
  target: string;
  /** `name`, `transform[.position|rotation|scale]`, `components.<key>[.<field>…]`; empty = the entity. */
  path?: string;
  value?: unknown;
  /** Removes the component (or, with an empty path, the entity). */
  removed?: boolean;
}

/** The `prefab` component of an entity, or null. */
export function prefabRef(entity: EntityDesc | null | undefined): { path: string; overrides?: PrefabOverride[] } | null {
  const p = entity?.components?.prefab;
  return p && typeof p === 'object' && typeof (p as { path?: unknown }).path === 'string'
    ? (p as { path: string; overrides?: PrefabOverride[] })
    : null;
}

/**
 * Scene entity owning prefab-instance entity `id`: the deepest entity with a
 * `prefab` component whose id prefixes it. Null for scene entities.
 */
export function prefabInstanceOf(doc: SceneDoc, id: string): { index: number; host: EntityDesc; target: string } | null {
  let best: { index: number; host: EntityDesc; target: string } | null = null;
  doc.entities.forEach((e, index) => {
    if (!prefabRef(e) || !id.startsWith(`${e.id}/`)) return;
    const target = id.slice(e.id.length + 1);
    if (target && (!best || target.length < best.target.length)) best = { index, host: e, target };
  });
  return best;
}

/** Replaces the override of `target` at `path` (dropping overrides of its sub-paths). */
export function upsertOverride(overrides: readonly PrefabOverride[], o: PrefabOverride): PrefabOverride[] {
  const path = o.path ?? '';
  const sub = `${path}.`;
  const kept = overrides.filter((x) => {
    if (x.target !== o.target) return true;
    const xp = x.path ?? '';
    return !(xp === path || xp.startsWith(sub) || (path === '' && o.removed));
  });
  const clean: PrefabOverride = { target: o.target };
  if (path) clean.path = path;
  if (o.removed) clean.removed = true;
  else if (o.value !== undefined && o.value !== null) clean.value = clone(o.value);
  return [...kept, clean];
}

/** `setPrefabChild`: an edit of a prefab-instance entity as an override. */
function prefabChildCommand(input: SceneDoc, command: EditorCommand): CommandResult | null {
  let id: string;
  let o: Omit<PrefabOverride, 'target'>;
  switch (command.cmd) {
    case 'setTransform':
      id = command.id;
      o = { path: 'transform', value: command.transform };
      break;
    case 'renameEntity':
      id = command.id;
      o = { path: 'name', value: command.name };
      break;
    case 'setComponent':
      id = command.id;
      o = command.value === null || command.value === undefined ? { path: `components.${command.key}`, removed: true } : { path: `components.${command.key}`, value: command.value };
      break;
    case 'removeComponent':
      id = command.id;
      o = { path: `components.${command.key}`, removed: true };
      break;
    case 'deleteEntity':
      id = command.id;
      o = { path: '', removed: true };
      break;
    default:
      return null;
  }
  if (indexOf(input, id) >= 0) return null;
  const owner = prefabInstanceOf(input, id);
  if (!owner) return null;
  const ref = prefabRef(owner.host)!;
  const overrides = upsertOverride(ref.overrides ?? [], { target: owner.target, ...o });
  const entities = input.entities.slice();
  entities[owner.index] = { ...owner.host, components: { ...owner.host.components, prefab: { ...ref, overrides } } };
  return { ok: true, doc: { ...input, entities } };
}

/** An entity id the editor can address: in the document or inside a prefab instance. */
export function entityExists(doc: SceneDoc, id: string): boolean {
  return indexOf(doc, id) >= 0 || prefabInstanceOf(doc, id) !== null;
}

/** Parent check: a parent inside a prefab instance counts as its instance for cycles. */
function parentProblem(doc: SceneDoc, child: string, parent: string): string | null {
  const instance = indexOf(doc, parent) < 0 ? prefabInstanceOf(doc, parent) : null;
  if (indexOf(doc, parent) < 0 && !instance) return `parent ${notFound(doc, parent)}`;
  const p = instance ? instance.host.id : parent;
  if (p === child || subtreeIds(doc, child).includes(p)) return `cannot parent '${child}' under '${parent}': that would create a cycle`;
  return null;
}

export function applySceneCommand(input: SceneDoc, command: EditorCommand): CommandResult {
  const fail = (error: string): CommandResult => ({ ok: false, error, doc: input });
  const taken = (doc: SceneDoc, id: string) => doc.entities.some((e) => e.id === id);
  const viaPrefab = prefabChildCommand(input, command);
  if (viaPrefab) return viaPrefab;

  switch (command.cmd) {
    case 'select': {
      for (const id of command.ids) {
        if (!entityExists(input, id)) return fail(notFound(input, id));
      }
      return { ok: true, doc: input };
    }
    case 'setTransform': {
      const i = indexOf(input, command.id);
      if (i < 0) return fail(notFound(input, command.id));
      const entities = input.entities.slice();
      entities[i] = { ...entities[i]!, transform: clone(command.transform) };
      return { ok: true, doc: { ...input, entities } };
    }
    case 'addEntity': {
      const desc: EntityDesc = clone(command.desc);
      if (command.parent !== undefined && command.parent !== null) desc.parent = command.parent;
      if (!desc.id || !desc.id.trim()) {
        desc.id = uniqueWith(slug(desc.name ?? 'entity'), (c) => taken(input, c));
      } else if (taken(input, desc.id)) {
        const free = uniqueWith(desc.id, (c) => taken(input, c));
        return fail(`id '${desc.id}' already exists; use a unique id such as '${free}'`);
      }
      if (desc.parent && !entityExists(input, desc.parent)) {
        return fail(`parent ${notFound(input, desc.parent)}`);
      }
      return { ok: true, doc: { ...input, entities: [...input.entities, desc] }, created: desc.id };
    }
    case 'deleteEntity': {
      if (indexOf(input, command.id) < 0) return fail(notFound(input, command.id));
      const gone = new Set(subtreeIds(input, command.id));
      return { ok: true, doc: { ...input, entities: input.entities.filter((e) => !gone.has(e.id)) } };
    }
    case 'renameEntity': {
      const i = indexOf(input, command.id);
      if (i < 0) return fail(notFound(input, command.id));
      const entities = input.entities.slice();
      entities[i] = { ...entities[i]!, name: command.name };
      return { ok: true, doc: { ...input, entities } };
    }
    case 'setComponent':
    case 'removeComponent': {
      const i = indexOf(input, command.id);
      if (i < 0) return fail(notFound(input, command.id));
      const entity = input.entities[i]!;
      const components: Components = { ...(entity.components ?? {}) };
      const value = command.cmd === 'setComponent' ? command.value : null;
      if (value === null || value === undefined) delete components[command.key];
      else components[command.key] = clone(value);
      const entities = input.entities.slice();
      entities[i] = { ...entity, components };
      return { ok: true, doc: { ...input, entities } };
    }
    case 'duplicate': {
      if (indexOf(input, command.id) < 0) return fail(notFound(input, command.id));
      const ids = subtreeIds(input, command.id);
      let rootNew: string;
      if (command.newId) {
        if (taken(input, command.newId)) return fail(`id '${command.newId}' already exists`);
        rootNew = command.newId;
      } else {
        rootNew = uniqueWith(`${command.id}_copy`, (c) => taken(input, c));
      }
      const map = new Map<string, string>([[command.id, rootNew]]);
      for (const old of ids.slice(1)) {
        const used = new Set(map.values());
        map.set(old, uniqueWith(`${old}_copy`, (c) => taken(input, c) || used.has(c)));
      }
      const copies = ids.map((old) => {
        const source = input.entities.find((e) => e.id === old)!;
        const copy: EntityDesc = { ...clone(source), id: map.get(old)! };
        if (old !== command.id) copy.parent = source.parent ? (map.get(source.parent) ?? null) : null;
        return copy;
      });
      const last = Math.max(...ids.map((id) => indexOf(input, id)));
      const entities = input.entities.slice();
      entities.splice(last + 1, 0, ...copies);
      return { ok: true, doc: { ...input, entities }, created: rootNew };
    }
    case 'reparent': {
      const i = indexOf(input, command.id);
      if (i < 0) return fail(notFound(input, command.id));
      const parent = command.parent ?? null;
      if (parent !== null) {
        const problem = parentProblem(input, command.id, parent);
        if (problem) return fail(problem);
      }
      const entities = input.entities.slice();
      entities[i] = { ...entities[i]!, parent };
      return { ok: true, doc: { ...input, entities } };
    }
    case 'terrainEdit': {
      const i = indexOf(input, command.id);
      if (i < 0) return fail(notFound(input, command.id));
      if (!('terrain' in (input.entities[i]!.components ?? {}))) return fail(`entity '${command.id}' has no terrain component`);
      return { ok: true, doc: input };
    }
    case 'paste': {
      const parent = command.parent ?? null;
      if (parent !== null && indexOf(input, parent) < 0) return fail(`paste parent: ${notFound(input, parent)}`);
      const pasted = new Set(command.entities.map((e) => e.id));
      const map = new Map<string, string>();
      for (const e of command.entities) {
        const used = new Set(map.values());
        const base = e.id && e.id.trim() ? e.id : slug(e.name ?? 'entity');
        map.set(e.id, uniqueWith(base, (c) => taken(input, c) || used.has(c)));
      }
      const parentInv = parent !== null && command.keepWorld !== false ? invertAffine(worldMatrix(input, parent)) : null;
      const out = command.entities.map((source) => {
        const e: EntityDesc = { ...clone(source), id: map.get(source.id)! };
        if (!(source.parent && pasted.has(source.parent))) {
          e.parent = parent;
          if (parentInv) e.transform = decomposeMatrix(multiply(parentInv, composeMatrix(source.transform)));
        } else {
          e.parent = map.get(source.parent) ?? null;
        }
        return e;
      });
      const at =
        parent !== null ? Math.max(...subtreeIds(input, parent).map((id) => indexOf(input, id))) + 1 : input.entities.length;
      const entities = input.entities.slice();
      entities.splice(at, 0, ...out);
      const doc = { ...input, entities };
      const createdIds = createdRoots(input, doc);
      // Explicit ids and transforms, so the engine produces the same document.
      const resolved: EditorCommand = {
        cmd: 'batch',
        commands: out.map((desc) => ({ cmd: 'addEntity', desc })),
      };
      return { ok: true, doc, created: createdIds[0], createdIds, resolved };
    }
    case 'batch': {
      let doc = input;
      const resolved: EditorCommand[] = [];
      for (const [i, c] of command.commands.entries()) {
        if (c.cmd === 'batch') return fail(`batch command ${i}: batches cannot be nested`);
        const r = applySceneCommand(doc, c);
        if (!r.ok) return fail(`batch command ${i}: ${r.error}`);
        doc = r.doc;
        const one = r.resolved ?? resolveIds(c, r.created);
        if (one.cmd === 'batch') resolved.push(...one.commands);
        else resolved.push(one);
      }
      const createdIds = createdRoots(input, doc);
      return { ok: true, doc, created: createdIds[0], createdIds, resolved: { cmd: 'batch', commands: resolved } };
    }
    default:
      return fail('unknown editor command');
  }
}

/** `command` with the id it generated written in (addEntity / duplicate). */
export function resolveIds(command: EditorCommand, created: string | undefined): EditorCommand {
  if (!created) return command;
  if (command.cmd === 'addEntity') return { ...command, desc: { ...command.desc, id: created } };
  if (command.cmd === 'duplicate') return { ...command, newId: created };
  return command;
}

export interface HierarchyRow {
  entity: EntityDesc;
  depth: number;
  hasChildren: boolean;
}

/**
 * Parent-before-child rows for the hierarchy panel, preserving document order
 * among siblings. Entities whose parent is missing are shown at the root so
 * nothing in the file is ever invisible.
 */
export function hierarchyRows(doc: SceneDoc, collapsed: ReadonlySet<string> = new Set()): HierarchyRow[] {
  const ids = new Set(doc.entities.map((e) => e.id));
  const children = new Map<string | null, EntityDesc[]>();
  for (const e of doc.entities) {
    const key = e.parent && ids.has(e.parent) && e.parent !== e.id ? e.parent : null;
    const bucket = children.get(key) ?? [];
    bucket.push(e);
    children.set(key, bucket);
  }
  const out: HierarchyRow[] = [];
  const seen = new Set<string>();
  const walk = (parent: string | null, depth: number) => {
    for (const entity of children.get(parent) ?? []) {
      if (seen.has(entity.id)) continue;
      seen.add(entity.id);
      const kids = children.get(entity.id) ?? [];
      out.push({ entity, depth, hasChildren: kids.length > 0 });
      if (!collapsed.has(entity.id)) walk(entity.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

/** Kind of entity, used for hierarchy icons. */
export function entityKind(entity: EntityDesc): 'light' | 'camera' | 'mesh' | 'terrain' | 'audio' | 'particles' | 'liquid' | 'empty' {
  const c = entity.components ?? {};
  if ('light' in c) return 'light';
  if ('camera' in c) return 'camera';
  if ('terrain' in c) return 'terrain';
  if ('mesh' in c || 'prefab' in c) return 'mesh';
  if ('particleSystem' in c) return 'particles';
  if ('liquid' in c) return 'liquid';
  if ('audioSource' in c) return 'audio';
  return 'empty';
}
